import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Search, Handshake, Zap, ArrowRight, CheckCircle, DollarSign, Clock, Star, Send, Package, Target } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';

function FeatureCard({ icon: Icon, title, children }) {
  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-8 card-hover">
      <div className="w-12 h-12 rounded-xl bg-copper/10 flex items-center justify-center mb-5">
        <Icon className="w-6 h-6 text-copper" aria-hidden="true" />
      </div>
      <h3 className="font-heading font-semibold text-lg text-navy mb-3">{title}</h3>
      <p className="text-slate-600 text-sm leading-relaxed">{children}</p>
    </article>
  );
}

export default function Buyers() {
  // Apply SEO meta tags
  useSEO('buyers');

  return (
    <Layout>
      {/* Hero */}
      <section 
        className="relative bg-slate-50 overflow-hidden"
        data-testid="buyers-hero"
        aria-labelledby="buyers-heading"
      >
        <div className="container-custom section-padding">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <header>
              <span className="inline-block text-copper font-semibold text-sm tracking-wider uppercase mb-4">
                For Beverage Buyers
              </span>
              <h1 
                id="buyers-heading"
                className="font-heading text-4xl md:text-5xl font-bold text-navy leading-tight mb-6"
              >
                Discover Curated Beverage Suppliers
              </h1>
              <p className="text-slate-600 text-lg leading-relaxed mb-8 max-w-xl">
                Buyer access is free. Browse curated suppliers by category and market, then request meetings or samples 
                through the coalition. Requests are routed and tracked to keep the network clean and responsive.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/buyer-match"
                  data-testid="buyers-cta-btn"
                  className="btn-primary"
                  aria-label="Get matched with qualified suppliers"
                >
                  <Send className="w-4 h-4 mr-2" aria-hidden="true" />
                  Get Matched
                </Link>
                <Link
                  to="/request-access"
                  data-testid="buyers-access-btn"
                  className="inline-flex items-center justify-center rounded-xl border-2 border-slate-200 text-navy font-semibold px-6 py-3 transition-all duration-200 hover:bg-slate-50"
                >
                  Request Buyer Access
                </Link>
              </div>
            </header>
            
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1729863254208-2fdc172a0bc6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxwcmVtaXVtJTIwY29ja3RhaWwlMjBiYXIlMjBiYXJ0ZW5kZXIlMjBzZXJ2aW5nJTIwZHJpbmt8ZW58MHx8fHwxNzcyMjUyNjg0fDA&ixlib=rb-4.1.0&q=85"
                  alt="Bar owner reviewing beverage menu, representing BevRelay buyer discovery"
                  className="w-full h-80 object-cover"
                  loading="eager"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-navy/10 rounded-full blur-2xl" aria-hidden="true" />
              <div className="absolute -top-6 -left-6 w-40 h-40 bg-copper/10 rounded-full blur-3xl" aria-hidden="true" />
            </div>
          </div>
        </div>
      </section>

      {/* Core Benefits */}
      <section 
        className="section-padding bg-white" 
        data-testid="buyers-features"
        aria-labelledby="buyer-benefits-heading"
      >
        <div className="container-custom">
          <header className="text-center mb-12">
            <h2 id="buyer-benefits-heading" className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-4">
              Why Buyers Love BevRelay
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Skip the cold outreach. Get connected to pre-vetted suppliers through trusted introductions at no cost.
            </p>
          </header>

          <div className="grid gap-8 md:grid-cols-3">
            <FeatureCard icon={Search} title="Curated Discovery">
              Multi-category portfolio with limited seats per category. 
              Only quality suppliers make the cut, so you can trust what you find.
            </FeatureCard>
            <FeatureCard icon={Handshake} title="Trusted Introductions">
              Requests route through the coalition to reduce noise and improve follow-through. 
              Real connections, not spam or cold outreach.
            </FeatureCard>
            <FeatureCard icon={Zap} title="Faster Sourcing">
              Get connected to qualified suppliers without endless research. 
              Save time, find better products, grow your business.
            </FeatureCard>
          </div>
        </div>
      </section>

      {/* Free Access Highlight */}
      <section 
        className="section-padding bg-slate-50" 
        data-testid="buyers-benefits"
        aria-labelledby="free-access-heading"
      >
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="rounded-2xl bg-white p-8 border border-slate-200 shadow-sm">
                <h3 className="font-heading font-semibold text-xl text-navy mb-6">
                  What You Get as a Buyer
                </h3>
                <ul className="space-y-4" role="list">
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-copper/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <div className="w-2 h-2 rounded-full bg-copper" aria-hidden="true" />
                    </div>
                    <span className="text-slate-600">Free access to curated supplier profiles</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-copper/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <div className="w-2 h-2 rounded-full bg-copper" aria-hidden="true" />
                    </div>
                    <span className="text-slate-600">Direct meeting and sample requests</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-copper/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <div className="w-2 h-2 rounded-full bg-copper" aria-hidden="true" />
                    </div>
                    <span className="text-slate-600">Tracked communication with suppliers</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-copper/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <div className="w-2 h-2 rounded-full bg-copper" aria-hidden="true" />
                    </div>
                    <span className="text-slate-600">Category filtering by market region</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-copper/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <div className="w-2 h-2 rounded-full bg-copper" aria-hidden="true" />
                    </div>
                    <span className="text-slate-600">Quality-assured supplier network</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <span className="inline-block text-copper font-semibold text-sm tracking-wider uppercase mb-4">
                100% Free Access
              </span>
              <h2 id="free-access-heading" className="font-heading text-3xl md:text-4xl font-semibold text-navy leading-tight mb-6">
                No Cost to Join, Real Value Delivered
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                Buyer access is free and approved to keep the network quality-focused. 
                No hidden fees, no subscriptions — just connections to the suppliers you need.
              </p>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 rounded-xl bg-green-50">
                  <DollarSign className="w-6 h-6 text-green-600 mx-auto mb-2" aria-hidden="true" />
                  <div className="text-sm font-semibold text-green-800">$0 Cost</div>
                </div>
                <div className="text-center p-4 rounded-xl bg-blue-50">
                  <Clock className="w-6 h-6 text-blue-600 mx-auto mb-2" aria-hidden="true" />
                  <div className="text-sm font-semibold text-blue-800">Fast Access</div>
                </div>
                <div className="text-center p-4 rounded-xl bg-amber-50">
                  <Star className="w-6 h-6 text-amber-600 mx-auto mb-2" aria-hidden="true" />
                  <div className="text-sm font-semibold text-amber-800">Quality Assured</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Buyer Types */}
      <section className="section-padding bg-white" aria-labelledby="buyer-types-heading">
        <div className="container-custom">
          <header className="text-center mb-12">
            <h2 id="buyer-types-heading" className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-4">
              Who Uses BevRelay
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Buyers across all channels discover new suppliers through the BevRelay network
            </p>
          </header>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center card-hover">
              <h3 className="font-semibold text-navy mb-2">Retailers</h3>
              <p className="text-sm text-slate-600">Liquor stores, grocery chains, specialty shops</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center card-hover">
              <h3 className="font-semibold text-navy mb-2">On-Premise</h3>
              <p className="text-sm text-slate-600">Bars, restaurants, hotels, nightclubs</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center card-hover">
              <h3 className="font-semibold text-navy mb-2">Chain Accounts</h3>
              <p className="text-sm text-slate-600">Regional and national chain buyers</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center card-hover">
              <h3 className="font-semibold text-navy mb-2">Distributors</h3>
              <p className="text-sm text-slate-600">Looking to expand portfolio offerings</p>
            </div>
          </div>
        </div>
      </section>

      {/* Buyer Match Feature */}
      <section className="section-padding bg-copper/5" aria-labelledby="buyer-match-heading" data-testid="buyer-match-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block text-copper font-semibold text-sm tracking-wider uppercase mb-4">
                New Feature
              </span>
              <h2 id="buyer-match-heading" className="font-heading text-3xl md:text-4xl font-semibold text-navy leading-tight mb-6">
                Need a Specific Product? Tell Us What You're Looking For
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                Submit your sourcing needs and we'll match you with qualified suppliers. 
                Whether it's spirits, wine, RTDs, co-packing, or private label — we'll connect you 
                with the right partners based on your requirements.
              </p>
              <div className="space-y-3 mb-8">
                {[
                  "Describe your product category and volume needs",
                  "We review and filter for best-fit suppliers",
                  "Get warm introductions, not cold lists"
                ].map((text, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-copper flex-shrink-0" />
                    <span className="text-slate-700">{text}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/buyer-match"
                className="btn-copper inline-flex items-center gap-2"
                data-testid="buyer-match-cta-btn"
              >
                <Send className="w-4 h-4" />
                Submit Match Request
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center">
                <Package className="w-8 h-8 text-copper mx-auto mb-3" />
                <h4 className="font-semibold text-navy mb-1">Co-Pack</h4>
                <p className="text-xs text-slate-500">Find manufacturing partners</p>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center">
                <Target className="w-8 h-8 text-copper mx-auto mb-3" />
                <h4 className="font-semibold text-navy mb-1">Private Label</h4>
                <p className="text-xs text-slate-500">Create your own brand</p>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center">
                <Search className="w-8 h-8 text-copper mx-auto mb-3" />
                <h4 className="font-semibold text-navy mb-1">Sourcing</h4>
                <p className="text-xs text-slate-500">Find new products</p>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center">
                <Handshake className="w-8 h-8 text-copper mx-auto mb-3" />
                <h4 className="font-semibold text-navy mb-1">Distribution</h4>
                <p className="text-xs text-slate-500">Expand your reach</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-slate-50" data-testid="buyers-cta" aria-labelledby="buyer-cta-heading">
        <div className="container-custom">
          <div className="rounded-3xl bg-navy p-10 md:p-16 text-center">
            <h2 id="buyer-cta-heading" className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">
              Ready to Find Your Next Supplier?
            </h2>
            <p className="text-white/80 max-w-2xl mx-auto mb-8">
              Tell us what you're looking for and we'll match you with qualified suppliers. 
              Free access, no commitments, quality assured.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/buyer-match"
                data-testid="buyers-bottom-cta-btn"
                className="btn-copper"
              >
                <Send className="w-4 h-4 mr-2" aria-hidden="true" />
                Get Matched
              </Link>
              <Link
                to="/target-list"
                className="inline-flex items-center justify-center rounded-xl border-2 border-white/30 text-white font-semibold px-6 py-3 transition-all duration-200 hover:bg-white/10"
              >
                Browse Target List
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
